import { useState, useEffect } from 'react';

// Assuming you have your Google Maps API key stored securely,
// perhaps in environment variables accessible during build time.
// Replace 'YOUR_GOOGLE_MAPS_API_KEY' with your actual key loading mechanism.
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'AIzaSyD9oIYmxhvGrQMjWk_IPlgoLbhvVJCCXdo';
const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-script';

type GoogleMapsStatus = 'idle' | 'loading' | 'ready' | 'error';

// Store the promise globally to avoid reloading the script
let loadPromise: Promise<void> | null = null;

export function useGoogleMaps(libraries: string[] = ['places', 'geocoding']): GoogleMapsStatus {
  const [status, setStatus] = useState<GoogleMapsStatus>('idle');

  useEffect(() => {
    if (window.google && window.google.maps) {
      // Already loaded by another instance or previous navigation
      setStatus('ready');
      return;
    }

    if (document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) {
       // Script tag exists, but might still be loading or failed
       // Rely on the global promise to resolve status
       if (loadPromise) {
           setStatus('loading');
           loadPromise.then(() => setStatus('ready')).catch(() => setStatus('error'));
       } else {
           // Should not happen if script exists, but handle defensively
           setStatus('error');
       }
       return;
    }

    setStatus('loading');

    if (!loadPromise) {
        loadPromise = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.id = GOOGLE_MAPS_SCRIPT_ID;
            script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=${libraries.join(',')}&callback=initMap`;
            script.async = true;
            script.defer = true;

            // Define the global callback function
            (window as any).initMap = () => {
                console.log('Google Maps API loaded successfully.');
                resolve();
            };

            script.onerror = (error) => {
                console.error('Error loading Google Maps script:', error);
                document.getElementById(GOOGLE_MAPS_SCRIPT_ID)?.remove(); // Clean up failed script
                loadPromise = null; // Allow retry
                reject(error);
            };

            document.head.appendChild(script);
        });
    }


    loadPromise
      .then(() => {
        setStatus('ready');
      })
      .catch(() => {
        setStatus('error');
      });

    // Cleanup function (optional, script usually stays loaded)
    // return () => {
    //   const script = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    //   if (script) {
    //     // document.head.removeChild(script); // Decide if you want to remove on unmount
    //     // delete (window as any).initMap;
    //   }
    // };
  }, [libraries]); // Re-run if libraries change (though usually static)

  return status;
}
